import {
  BufferTarget,
  CanvasSource,
  Mp4OutputFormat,
  Output,
} from 'mediabunny';
import type { MovieFrame, Project } from '../domain/model';
import { SPEEDS } from '../domain/model';

export interface ExportResult {
  blob: Blob;
  extension: 'mp4' | 'webm';
  mimeType: string;
}

export type ExportProgress = (completed: number, total: number) => void;

export class MovieExportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MovieExportError';
  }
}

async function drawBlob(canvas: HTMLCanvasElement, blob: Blob): Promise<void> {
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new MovieExportError('This device could not prepare the movie pictures.');
  if ('createImageBitmap' in globalThis) {
    const bitmap = await createImageBitmap(blob);
    context.fillStyle = '#000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return;
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    context.fillStyle = '#000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function exportCanvas(frames: MovieFrame[]): HTMLCanvasElement {
  const first = frames[0];
  if (!first) throw new MovieExportError('Take at least two pictures before making a movie.');
  const canvas = document.createElement('canvas');
  canvas.width = first.width - (first.width % 2);
  canvas.height = first.height - (first.height % 2);
  return canvas;
}

export function selectRecordingMimeType(isSupported: (type: string) => boolean): string | undefined {
  return [
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ].find(isSupported);
}

async function exportWithWebCodecs(
  project: Project,
  frames: MovieFrame[],
  progress: ExportProgress,
): Promise<ExportResult> {
  if (!('VideoEncoder' in globalThis)) throw new MovieExportError('Fast MP4 export is not available.');
  const fps = SPEEDS[project.speed].fps;
  const canvas = exportCanvas(frames);
  const target = new BufferTarget();
  const output = new Output({ format: new Mp4OutputFormat(), target });
  const source = new CanvasSource(canvas, { codec: 'avc', bitrate: 2_500_000 });
  output.addVideoTrack(source, { frameRate: fps });
  await output.start();
  try {
    for (let index = 0; index < frames.length; index += 1) {
      const frame = frames[index];
      if (!frame) continue;
      await drawBlob(canvas, frame.blob);
      await source.add(index / fps, 1 / fps, { keyFrame: index % fps === 0 });
      progress(index + 1, frames.length);
    }
    source.close();
    await output.finalize();
    if (!target.buffer) throw new MovieExportError('The movie file came out empty.');
    return { blob: new Blob([target.buffer], { type: 'video/mp4' }), extension: 'mp4', mimeType: 'video/mp4' };
  } catch (error) {
    await output.cancel().catch(() => undefined);
    throw error;
  }
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function exportWithMediaRecorder(
  project: Project,
  frames: MovieFrame[],
  progress: ExportProgress,
): Promise<ExportResult> {
  if (!('MediaRecorder' in globalThis) || !HTMLCanvasElement.prototype.captureStream) {
    throw new MovieExportError('Movie export is not supported by this browser. Try current Chrome, Safari, or Edge.');
  }
  const mimeType = selectRecordingMimeType((type) => MediaRecorder.isTypeSupported(type));
  if (!mimeType) throw new MovieExportError('This device does not have a supported movie format.');

  const fps = SPEEDS[project.speed].fps;
  const frameTime = 1000 / fps;
  const canvas = exportCanvas(frames);
  const stream = canvas.captureStream(fps);
  const chunks: Blob[] = [];
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
  const finished = new Promise<void>((resolve, reject) => {
    recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
    recorder.onstop = () => resolve();
    recorder.onerror = () => reject(new MovieExportError('Movie making was interrupted. Please try again.'));
  });
  recorder.start();
  try {
    for (let index = 0; index < frames.length; index += 1) {
      const frame = frames[index];
      if (!frame) continue;
      await drawBlob(canvas, frame.blob);
      progress(index + 1, frames.length);
      await wait(frameTime);
    }
    recorder.stop();
    await finished;
  } finally {
    stream.getTracks().forEach((track) => track.stop());
    if (recorder.state !== 'inactive') recorder.stop();
  }
  const extension = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
  const simpleMime = extension === 'mp4' ? 'video/mp4' : 'video/webm';
  const blob = new Blob(chunks, { type: simpleMime });
  if (!blob.size) throw new MovieExportError('The movie file came out empty. Please try again.');
  return { blob, extension, mimeType: simpleMime };
}

export async function exportMovie(
  project: Project,
  frames: MovieFrame[],
  progress: ExportProgress,
): Promise<ExportResult> {
  try {
    return await exportWithWebCodecs(project, frames, progress);
  } catch {
    progress(0, frames.length);
    try {
      return await exportWithMediaRecorder(project, frames, progress);
    } catch (error) {
      if (error instanceof MovieExportError) throw error;
      throw new MovieExportError('The movie could not be made on this device. Your pictures are still safe.');
    }
  }
}

